/*
 * Proton PRE-ORDER storefront layer — 2026-08-07
 *
 * Built for Project Coyote, but generic: add a handle to HANDLES and it works.
 *
 * WHY THIS EXISTS
 *   A Shopify "pre-order" is really just `inventoryPolicy: CONTINUE` + 0 on hand.
 *   Natively the storefront tells the shopper NOTHING — the button still says
 *   "Add to cart", the card looks in-stock, and the cart line is indistinguishable
 *   from a paddle sitting in the warehouse. Diego's requirement was the opposite:
 *   "preorder only so that they know it's not actually there." This is that layer.
 *
 * ⚠️ 2026-08-25 — THE NAME OF THIS FILE IS NOW A HISTORICAL ARTEFACT.
 *   The first production run landed, so the paddle it decorates is IN STOCK and
 *   the wording below says so. The plumbing (handle scoping, card/cart hooks,
 *   the order stamp, the cleaner suppression) is unchanged and still correct —
 *   only the vocabulary moved. See the block above LABEL/PILL for what the
 *   strings are now and what has to move together if it ever goes back.
 *
 * WHAT IT DOES
 *   1. PDP  — a notice card is injected under the buy buttons, and a hidden
 *             line-item property is added to the form so every order line is
 *             stamped (shows on the order + pick list). The BUTTON is left
 *             alone while LABEL is null; set LABEL to a string to override it.
 *   2. GRID — a status pill on any product card linking to a listed handle.
 *   3. CART — the same pill on the line + a short notice.
 *   4. Suppresses the RESET cleaner upsell on a pre-order PDP (it is a Shopify
 *      Collective dropship item; pairing it with a pre-order guarantees a split
 *      shipment and the "SHIPS SEPARATELY" pick-list blindspot).
 *
 * SAFE BY CONSTRUCTION: everything is scoped to HANDLES. On every other page in
 * the store this script observes nothing and changes nothing.
 *
 * Deploy: node ~/proton/scripts/_deploy-proton-preorder.js
 * Remove: node ~/proton/scripts/_deploy-proton-preorder.js --remove
 */
(function () {
  "use strict";

  // 2026-09-10: product handle renamed project-coyote-preorder -> project-coyote
  // (Diego: clean the "pre-order" out of the main product link). BOTH are listed
  // so this layer keeps matching across the rename + the old-handle 301 redirect.
  var HANDLES = ["project-coyote", "project-coyote-preorder"];

  // Ship window shown on the PDP notice. Keep it in sync with the product
  // description — ~/proton/scripts/coyote-set-ship-window.js rewrites BOTH (plus
  // the homepage card) in one command. null => the notice omits the line.
  // 🚩 Diego, 2026-08-21: "Ships within 2 weeks" -> "Shipping soon". A stated
  //    number of weeks is a delivery promise the production run cannot
  //    underwrite; the soft phrase carries no duration to go stale against.
  var SHIP_TEXT = "Low stock";

  // The long-form build story lives on its own page. Without a hook from the
  // PDP nothing on the storefront links to it, so it only ever gets traffic
  // from ads. Set to null to pull the link without touching anything else.
  var LANDING_URL = "/pages/coyote-preorder";
  var LANDING_LABEL = "See how it's built";

  // A second link-out, to the whole Coyote collection (bundles, tee, cap).
  // Diego 2026-08-26: "on the coyote product page also have a link to the
  // collection." The PDP takes ~48x the collection page's traffic yet linked
  // there zero times — this is the entry point that was missing. Set to null
  // to pull it without touching anything else.
  var COLLECTION_URL = "/pages/coyote-collection";
  var COLLECTION_LABEL = "See the collection";

  // 2026-09-21 (Diego, on the Coyote BAG PDP): "Add the 'See the collection'
  // button here as well, like on the Coyote paddle page." The backpack gets the
  // collection link and NOTHING ELSE — no pre-order notice (it is not a
  // pre-order), no "See how it's built" (that page is the paddle's anatomy), no
  // order-line stamp and no button override. So these handles route to a
  // deliberately minimal sibling of decoratePDP() — see decorateCollectionOnlyPDP().
  var COLLECTION_ONLY_HANDLES = ["atomic-pickleball-backpack"];
  // The bag PDP points at the WHOLE collection, so the sub-copy names the range
  // rather than the paddle's tee/cap (which is what the paddle PDP's line says).
  var COLLECTION_ONLY_SUB = "The paddle, the bundles and the gear.";

  // ── 2026-08-25 (Diego, with a screenshot of the grid card): "also change
  //    this from pre order to IN STOCK" ────────────────────────────────────
  // The first production run landed. 439 units are unallocated on the live
  // variant and 6 have already been fulfilled, so the pre-order framing this
  // whole layer was built to enforce has outlived the fact it described.
  //
  // LABEL is now null ON PURPOSE. The honest button text is the theme's own
  // "Add to cart" — the point of overriding it was to warn a shopper the
  // paddle was not on a shelf, and it IS on a shelf. Setting LABEL to a new
  // string would just be a different override with no reason behind it; null
  // makes decoratePDP() leave Dawn's button alone entirely.
  //
  // 🚨 THESE THREE MUST AGREE. The card pill, the buy button and the cart
  // line are read within seconds of each other. A card that says IN STOCK
  // over a button that says PRE-ORDER NOW is the same self-contradiction the
  // collection page shipped this morning — see check_stock_claim_coherence()
  // in ~/proton-coyote-collection/build.py.
  //
  // If the line ever sells through, this layer does NOT heal itself — it has
  // no inventory input at all. Shopify's own sold-out state is respected in
  // decoratePDP() (see the "sold out"/"unavailable" guard), but the PILL is
  // unconditional. Put the pre-order wording back by hand, or delete the
  // pill, before re-opening the paddle for pre-order.
  var LABEL = null;
  // 2026-09-04 (Diego): "change the icon of in stock to free hat on the coyote."
  // The Coyote's grid-card + cart-line pill now promotes the Labor Day free hat
  // instead of the (redundant) stock claim. This is a PROMO badge, not a stock
  // claim, so it does not create the IN-STOCK-vs-PRE-ORDER contradiction the note
  // above guards against. Revert to "IN STOCK" (or the pre-order wording) if the
  // free-hat promo ends.
  // 2026-09-08 (Diego): Labor Day over — reverted the promo "FREE HAT" badge
  // back to the stock claim. The paddle is in stock.
  var PILL = "IN STOCK";
  // 🚨 2026-09-02 (Brant → Ryder, forwarded by Diego): "remove the preorder
  // messaging, it's causing confusion." The paddle is IN STOCK, but every order
  // line was still stamped a NON-underscore property `Pre-order: Coyote drop`,
  // which Shopify PRINTS to the customer in the cart, at checkout and on the
  // order/packing slip. Prefixing the name with `_` keeps the internal stamp on
  // the order (visible in the Shopify admin, useful to tag drop orders) while
  // hiding it from every customer-facing surface — that is the whole fix.
  // Nothing in the codebase reads this property: the pick list classifies from
  // product id / -PRE sku / variant title (IS_PREORDER in lib/preorder-classify.js).
  // 🚩 The landing page /pages/coyote-preorder posts the SAME property from its
  //    own hidden input — page.src.liquid line ~355. RENAME BOTH TOGETHER OR
  //    NEITHER, or orders stamp two different names depending on entry page.
  var PROP_NAME = "_Pre-order";
  // Internal marker value, now hidden from the customer by the `_` name prefix.
  var PROP_VALUE = "Coyote drop";

  var ORANGE = "#FF6000";
  var CREAM = "#DECAA4";
  var CHARCOAL = "#0B0B0D";

  function log() {
    try { console.info.apply(console, ["[proton-preorder]"].concat([].slice.call(arguments))); } catch (e) {}
  }

  function isPreorderHref(href) {
    if (!href) return false;
    for (var i = 0; i < HANDLES.length; i++) {
      if (href.indexOf("/products/" + HANDLES[i]) !== -1) return true;
    }
    return false;
  }

  // ⚠️ Do NOT detect the PDP from location.pathname alone. Shopify's draft-product
  // preview serves the real product template at /products_preview?preview_key=…,
  // so a path test reports "not a PDP" on exactly the URL you verify with.
  // ShopifyAnalytics.meta.product.handle is printed natively on every product page
  // and is correct in both cases.
  function currentProductHandle() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && m.product.handle) return m.product.handle;
    } catch (e) {}
    try {
      var c = document.querySelector('link[rel="canonical"]');
      if (c && c.href) {
        var mm = c.href.match(/\/products\/([^/?#]+)/);
        if (mm) return mm[1];
      }
    } catch (e) {}
    var mp = (location.pathname || "").match(/\/products\/([^/?#]+)/);
    return mp ? mp[1] : null;
  }

  function onPreorderPDP() {
    var h = currentProductHandle();
    if (!h) return false;
    for (var i = 0; i < HANDLES.length; i++) if (HANDLES[i] === h) return true;
    return false;
  }

  function onCollectionOnlyPDP() {
    var h = currentProductHandle();
    if (!h) return false;
    for (var i = 0; i < COLLECTION_ONLY_HANDLES.length; i++) {
      if (COLLECTION_ONLY_HANDLES[i] === h) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------- styles
  function injectCSS() {
    if (document.getElementById("proton-preorder-css")) return;
    var s = document.createElement("style");
    s.id = "proton-preorder-css";
    s.textContent = [
      // Diego 09-10: "fix this awkward gap" — Dawn gives .product__description a
      // 26px margin-bottom that stacks onto the learn-cards' own 16px top margin.
      // Tighten it on the Coyote PDP only (body class set in decoratePDP) so the
      // Specifications/Description dropdowns sit flush with "See how it's built".
      "body.pp-coyote .product__description{margin-bottom:0 !important}",
      "body.pp-coyote .pp-learn{margin-top:0 !important}",
      ".pp-pill{display:inline-block;font:700 11px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;",
      "letter-spacing:.16em;text-transform:uppercase;color:#fff;background:" + ORANGE + ";",
      "padding:6px 10px;border-radius:999px;white-space:nowrap;vertical-align:middle}",
      ".pp-card-pill{position:absolute;top:10px;left:10px;z-index:3;pointer-events:none}",
      ".pp-note{border:1px solid " + ORANGE + ";background:" + CHARCOAL + ";border-radius:12px;",
      "padding:14px 16px;margin:14px 0 4px;color:" + CREAM + ";",
      "font:400 14px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}",
      ".pp-note b{color:#fff}",
      ".pp-note .pp-h{display:block;color:" + ORANGE + ";font-weight:700;font-size:11px;",
      "letter-spacing:.16em;text-transform:uppercase;margin-bottom:6px}",
      ".pp-cart-note{margin-top:6px;color:#8a8175;font-size:12px;line-height:1.4}",
      // ── 2026-09-10 (Diego: "cleaner, not as bubbly and AI-like and colorful") ──
      // WAS: a charcoal panel with a 1px orange border, 12px radius and an
      // inset orange glow that grew on hover. That was the loudest thing in the
      // buy box and used the exact glow language stripped out of the trust strip
      // the same day, so the buy box read in two visual languages at once.
      //
      // 🚨 The OLD comment here said the dark ground was load-bearing — "a
      // tinted-orange panel with cream type is invisible on white". That was
      // TRUE and it is the trap: the ground and the ink are ONE decision. Going
      // light means CREAM TYPE MUST DIE TOO. Both links are now re-inked to the
      // same navy the trust strip uses; nothing here may reference CREAM again.
      //
      // Now: a bordered-bottom list row (the iOS settings-list pattern) — reads
      // as tappable without being a bubble. Orange survives ONLY on the chevron.
      ".pp-learn{display:flex;align-items:center;justify-content:space-between;gap:14px;",
      "margin:16px 0 0;padding:13px 2px 13px 0;border:0;",
      "border-top:1px solid rgba(27,41,66,.16);border-bottom:1px solid rgba(27,41,66,.16);",
      "border-radius:0;background:none;box-shadow:none;text-decoration:none;color:#1b2942;",
      // no font-family — inherit the theme's Montserrat so it matches the strip
      "font-weight:600;font-size:13.5px;line-height:1.35;transition:opacity .18s ease}",
      // The two cards are adjacent siblings. Only the first carries top space,
      // and the second drops its top rule so the shared edge draws ONCE — two
      // touching 1px borders render as a 2px line and read as a mistake.
      ".pp-learn + .pp-learn{margin-top:0;border-top:0}",
      ".pp-learn:hover{opacity:.72}",
      ".pp-learn small{display:block;margin-top:3px;font-weight:500;font-size:12px;",
      "color:rgba(27,41,66,.6)}",
      // flush-right at 0px measured as "not clipped" but READS clipped against
      // the screen edge on mobile — the row carries 2px and the glyph has weight.
      ".pp-learn i{font-style:normal;color:" + ORANGE + ";font-size:20px;line-height:1;font-weight:700}",
      // 🚨 the collection card's thumbs come from proton-upsell.js and were
      // bordered CREAM for the dark ground — white circles on a cream border
      // vanish on the light PDP. Scoped override (0,2,1 beats 0,1,1, so load
      // order does not matter).
      ".pp-learn .pu-tile-thumbs span{border-color:rgba(27,41,66,.14)}",
      "@media (prefers-reduced-motion:reduce){.pp-learn{transition:none}}"
    ].join("");
    document.head.appendChild(s);
  }

  // ---------------------------------------------------------------- PDP
  function decoratePDP() {
    document.body.classList.add("pp-coyote"); // scopes the gap fix above to this PDP
    // Every add-to-cart button on the page, including sticky/quick bars.
    // LABEL === null means "do not touch the button" — the theme's native
    // wording is correct and this layer has nothing to add. Skipping the
    // whole loop (rather than assigning an empty string) leaves the button
    // byte-for-byte as Dawn rendered it, spinner spans and all.
    var btns = LABEL ? document.querySelectorAll('form[action*="/cart/add"] button[name="add"], form[action*="/cart/add"] [type="submit"]') : [];
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.getAttribute("data-pp") === "1") continue;
      var span = b.querySelector("span:not(.visually-hidden):not(.loading-overlay__spinner)") || b;
      // Never overwrite Shopify's own sold-out / unavailable state.
      var txt = (span.textContent || "").trim().toLowerCase();
      if (txt.indexOf("sold out") !== -1 || txt.indexOf("unavailable") !== -1) continue;
      span.textContent = LABEL;
      b.setAttribute("data-pp", "1");
    }

    // Stamp the order line so fulfilment can see it without opening the product.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var j = 0; j < forms.length; j++) {
      var f = forms[j];
      if (f.querySelector('input[data-pp-prop="1"]')) continue;
      var inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "properties[" + PROP_NAME + "]";
      inp.value = PROP_VALUE;
      inp.setAttribute("data-pp-prop", "1");
      f.appendChild(inp);
    }

    // The notice, directly under the buy buttons — but only if the product
    // description isn't already carrying one. The description block is the
    // server-rendered source of truth (it also travels to Shop / Google / email);
    // this JS note is the fallback for themes that bury the description in a tab.
    // ⚠️ Do NOT test on a class alone: the storefront can serve a cached copy of
    // an older description for up to ~60 min after a write, so the class may be
    // missing on a product that genuinely has the block. Test the text.
    // 🚩 THE DETECTOR MUST NOT BE PINNED TO ONE SENTENCE. It used to test the
    // description for the single string /not in stock/i — the exact phrase the
    // notice above stopped printing on 2026-08-25 (it was false and banned).
    // Any description still carrying the OLD server-rendered wording must keep
    // suppressing the JS copy or the shopper sees the notice twice, so the old
    // phrase stays in this list forever; the new wording is added beside it.
    // Detection is not copy — retiring a phrase from the page does not retire it
    // from here.
    var NOTICE_MARKERS = /not in stock|first production run|in the order they were placed/i;
    var descHasNotice = false;
    try {
      var desc = document.querySelector(".product__description, .product-single__description, [class*='product__description']");
      if (desc && NOTICE_MARKERS.test(desc.textContent || "")) descHasNotice = true;
    } catch (e) {}

    if (!document.querySelector(".pp-note") && !descHasNotice) {
      var host = document.querySelector(".product-form__buttons") ||
                 document.querySelector('form[action*="/cart/add"]');
      if (host) {
        var d = document.createElement("div");
        d.className = "pp-note";
        // ── 2026-08-25: TWO FALSE STATEMENTS REMOVED FROM THIS BLOCK ─────────
        //
        // It used to read: `Pre-order · not in stock yet` / "This paddle has not
        // shipped yet. You are reserving one from the first production run.
        // Pre-orders are filled in the order they were placed. Shipping now."
        //
        // 🚨 BOTH OF THE FIRST TWO CLAIMS WERE FLATLY UNTRUE by the time the drop
        // went live, and they sat in the same paragraph as the sentence that
        // contradicted them:
        //   · "not in stock yet"          — 994 units on hand, 439 unallocated.
        //   · "has not shipped yet"       — 6 units already fulfilled.
        //   · "…Shipping now."            — the authorised window, printed
        //                                   immediately after "has not shipped".
        // A shopper read "has not shipped yet. … Shipping now." in one breath,
        // one click from a homepage badge saying NOW IN STOCK.
        //
        // 🚩 "not in stock yet" was ALSO already banned. The standing-copy note
        // ~70 lines below has said "NOT IN STOCK / NOT IN STOCK YET is forbidden
        // storefront-wide" since the cart pill was written — but it was written
        // as a comment ON the cart pill, so it read as a rule about that one
        // string and this block kept printing the banned phrase in a <b> header.
        // A ban documented next to one caller is not a ban.
        //
        // What is left is the part that is still TRUE and still worth disclosing:
        // 555 units are queued ahead of a new order, and they are filled in the
        // order they were placed. That is real information a buyer wants, it does
        // not contradict "in stock", and it does not require deciding whether the
        // product is still a "pre-order" — which is Diego's call, not this file's.
        d.innerHTML = '<span class="pp-h">Low stock</span>' +
          "Orders are filled in the order they were placed. Once this batch is gone, new orders ship with the next one." +
          (SHIP_TEXT ? " <b>" + SHIP_TEXT + ".</b>" : "");
        host.parentNode.insertBefore(d, host.nextSibling);
      }
    }

    // Link out to the long-form build page, directly under the buy buttons.
    // If the JS notice above was the one that rendered it is a sibling of the
    // buy block, so the link goes after IT and the order reads buy -> terms ->
    // learn more. When the server-rendered .pp-note is used instead it lives
    // inside the product description, far down the DOM — `contains` is false,
    // and the link correctly stays anchored to the buy buttons rather than
    // being flung into the middle of the spec list.
    if (LANDING_URL && !document.querySelector(".pp-learn")) {
      var buy = document.querySelector(".product-form__buttons") ||
                document.querySelector('form[action*="/cart/add"]');
      if (buy) {
        var note = document.querySelector(".pp-note");
        var anchorEl = (note && buy.parentNode.contains(note)) ? note : buy;
        var a = document.createElement("a");
        a.className = "pp-learn";
        a.href = LANDING_URL;
        a.innerHTML = "<span>" + LANDING_LABEL +
          "<small>The hybrid core, the throat taper, the colourway.</small></span><i>&rsaquo;</i>";
        anchorEl.parentNode.insertBefore(a, anchorEl.nextSibling);
      }
    }

    // A second link-out to the whole Coyote collection, under the build link.
    // Order reads buy -> terms -> how it's built -> the collection. It reuses
    // the .pp-learn dark-ground styling (adds .pp-collection for the guard) so
    // it doesn't wash out on the light PDP. Anchors after the build link when
    // present, else the note/buy. Diego 2026-08-26.
    if (COLLECTION_URL && !document.querySelector(".pp-collection")) {
      var cAnchor = document.querySelector(".pp-learn") ||
                    document.querySelector(".pp-note") ||
                    document.querySelector(".product-form__buttons") ||
                    document.querySelector('form[action*="/cart/add"]');
      if (cAnchor) {
        var ca = document.createElement("a");
        ca.className = "pp-learn pp-collection";
        ca.href = COLLECTION_URL;
        ca.innerHTML = "<span>" + COLLECTION_LABEL +
          "<small>The bundles, the tee and the cap.</small></span><i>&rsaquo;</i>";
        cAnchor.parentNode.insertBefore(ca, cAnchor.nextSibling);
      }
    }
  }

  // ------------------------------------------------- collection-only PDP (bag)
  // The Coyote BAG PDP: inject ONLY the "See the collection" link, styled exactly
  // like the paddle PDP's (reuses .pp-learn + .pp-collection). No notice, no build
  // link, no button/stamp — see COLLECTION_ONLY_HANDLES above for why.
  function decorateCollectionOnlyPDP() {
    if (!COLLECTION_URL || document.querySelector(".pp-collection")) return;
    var cAnchor = document.querySelector(".product-form__buttons") ||
                  document.querySelector('form[action*="/cart/add"]');
    if (!cAnchor) return;
    var ca = document.createElement("a");
    ca.className = "pp-learn pp-collection";
    ca.href = COLLECTION_URL;
    ca.innerHTML = "<span>" + COLLECTION_LABEL +
      "<small>" + COLLECTION_ONLY_SUB + "</small></span><i>&rsaquo;</i>";
    cAnchor.parentNode.insertBefore(ca, cAnchor.nextSibling);
  }

  // ---------------------------------------------------------------- grid cards
  function decorateCards() {
    // On the pre-order product's own page every "view details" link matches, and
    // a loose ancestor lookup lands the pill on top of the product title. The PDP
    // has its own treatment — cards are a grid-only concern.
    if (onPreorderPDP()) return;
    var links = document.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (!isPreorderHref(a.getAttribute("href"))) continue;
      // Require Dawn's real card structure. No .grid__item fallback: that matches
      // the main product section on a PDP.
      var card = a.closest(".card-wrapper") || a.closest(".card");
      if (!card || card.querySelector(".pp-card-pill")) continue;
      var media = card.querySelector(".card__media");
      if (!media) continue;
      // ⚠️ never put filter/transform on .card-wrapper — it breaks the quick-add
      // modal (see memory: storefront-soldout-dim). position:relative is safe.
      var cs = window.getComputedStyle(media);
      if (cs.position === "static") media.style.position = "relative";
      var pill = document.createElement("span");
      pill.className = "pp-pill pp-card-pill";
      pill.textContent = PILL;
      media.appendChild(pill);
    }
  }

  // ---------------------------------------------------------------- cart
  // 2026-09-10 (Diego, re: cart drawer screenshot): "remove this in-stock stuff
  // from the checkout. It only needs to mark it if it's out of stock... this
  // wouldn't need anything. I think it'll make the checkout cleaner."
  //
  // The paddle IS in stock, so stamping an "IN STOCK" pill + a queue-order note
  // on the cart line is telling the shopper something true but redundant right
  // when they're trying to check out — pure clutter. Dropped entirely rather
  // than gated on an availability check: this cart line only ever renders for
  // add-to-cart-able quantities of a HANDLES product, and Shopify's own line-item
  // UI already carries the native "unavailable"/quantity-adjusted messaging if a
  // sold line ever goes stale in someone's cart, so there is nothing left for
  // this layer to usefully add here. decorateCards() (grid) and decoratePDP()
  // are untouched — Diego's ask was specifically about the cart/checkout view.
  function decorateCart() {}

  // ---------------------------------------------------------------- ship bubble
  // 2026-10-20 Diego: Coyote paddle is sold through with a shipment inbound —
  // keep it ORDERABLE (inventoryPolicy CONTINUE) and put a small orange bubble
  // directly above the buy button, same look as the wholesale dealer grid
  // ("Shipping within 5–7 business days"). Paddle + every bundle containing the
  // paddle. Remove = empty SHIP_BUBBLE_HANDLES (or SHIP_BUBBLE_TEXT = "").
  var SHIP_BUBBLE_TEXT = ""; // 10-28 Diego: removed (was "Ships within 5\u20137 business days")
  var SHIP_BUBBLE_HANDLES = ["project-coyote", "project-coyote-preorder",
    "coyote-bundle-tee", "coyote-bundle-hat", "coyote-bundle-tee-hat",
    "coyote-bundle-full", "coyote-kit", "coyote-peacock-partner-pack"];
  function onShipBubblePDP() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return false;
    return SHIP_BUBBLE_HANDLES.indexOf(decodeURIComponent(m[1]).toLowerCase()) !== -1;
  }
  function decorateShipBubble() {
    if (!SHIP_BUBBLE_TEXT || !onShipBubblePDP()) return;
    if (!document.getElementById("pp-ship-bubble-css")) {
      var st = document.createElement("style");
      st.id = "pp-ship-bubble-css";
      st.textContent =
        ".pp-ship-bubble-wrap{display:flex;justify-content:center;margin:2px 0 10px}" +
        ".pp-ship-bubble{position:relative;display:inline-flex;align-items:center;gap:7px;padding:6px 14px;border-radius:999px;" +
        "font-size:13px;font-weight:700;letter-spacing:.01em;line-height:1.2;color:#fff;" +
        "background:linear-gradient(135deg,#f59e0b,#ea580c);box-shadow:0 4px 16px rgba(234,88,12,.32)}" +
        ".pp-ship-bubble::after{content:'';position:absolute;left:50%;top:100%;transform:translateX(-50%);" +
        "border:6px solid transparent;border-top-color:#ea580c}" +
        ".pp-ship-bubble svg{width:15px;height:15px;flex:none}";
      document.head.appendChild(st);
    }
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var btns = forms[i].querySelector(".product-form__buttons");
      var sub = forms[i].querySelector('button[name="add"], [type="submit"]');
      var anchor = btns || sub;
      if (!anchor || !anchor.offsetParent) continue;
      if (anchor.previousElementSibling && anchor.previousElementSibling.classList &&
          anchor.previousElementSibling.classList.contains("pp-ship-bubble-wrap")) continue;
      var w = document.createElement("div");
      w.className = "pp-ship-bubble-wrap";
      w.innerHTML = '<span class="pp-ship-bubble"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="17" cy="17.5" r="1.6"/></svg>' +
        SHIP_BUBBLE_TEXT + "</span>";
      anchor.parentNode.insertBefore(w, anchor);
    }
  }

  // ---------------------------------------------------------------- boot
  function run() {
    injectCSS();
    try { decorateShipBubble(); } catch (e) {}
    if (onPreorderPDP()) {
      // The RESET cleaner is a dropship accessory. Offering it on a pre-order
      // page manufactures a split shipment; stand it down here only.
      window.PROTON_CLEANER_ENABLED = false;
      decoratePDP();
    } else if (onCollectionOnlyPDP()) {
      // Bag PDP — collection link only (Diego 2026-09-21).
      decorateCollectionOnlyPDP();
    }
    decorateCards();
    decorateCart();
  }

  function boot() {
    run();
    try {
      var mo = new MutationObserver(function () {
        if (mo._t) return;
        mo._t = setTimeout(function () { mo._t = null; run(); }, 120);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    document.addEventListener("cart:refresh", run);
    document.addEventListener("cart:updated", run);
    log("booted", HANDLES.join(","), "pdp=" + onPreorderPDP());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.ProtonPreorder = { run: run, handles: HANDLES, _booted: true };
})();
